import { Button, Form, Input, message, Spin } from "antd";
import axios, { AxiosError } from "axios";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createMachineApi } from "../axios/portalData";
import { downloadFile, getGroupAndMachineDocuments } from "../Common/commonFunctions";
import { sampleMessage } from "../Common/commonVariables";
import CreateNewGroupPopup from "../Components/CreateNewGroup/createNewGroupPopup";
import Dropdown from "../Components/Dropdown/Dropdown";
import Download from "../Icons/Download";
import GroupDocumentContext from "../themeContext/GroupDocumentContext";
import GroupsMachinesReFetchFlagContext from "../themeContext/GroupsMachinesReFetchFlagContext";
import MachineDocumentContext from "../themeContext/MachineDocumentContext";
import SelectedGroupContext from "../themeContext/selectedGroupContext";
import UserContext from "../themeContext/UserContext";

interface FormSubmit {
  displayName: string;
  group: string;
  serial: string;
}

const NewMachine: React.FC = () => {
  // const { Option } = Select;
  // const [currentGroupValue, setCurrentGroupValue] = useState("None");
  const [isLoading, setIsLoading] = useState(false);
  const [isGroupModalVisible, setIsGroupModalVisible] = useState(false);
  const [groups, setGroups] = useState<string[]>([]);
  const [isGroupDropdownLoading, setIsGroupDropdownLoading] = useState(false);
  const { selectedGroup, setSelectedGroup } = useContext(SelectedGroupContext);
  const { userInfo } = useContext(UserContext);
  const accountType = userInfo.accountType;
  const { groupDocument, setGroupDocument } = useContext(GroupDocumentContext);
  const { setMachineDocuments } = useContext(MachineDocumentContext);
  const { setGroupsMachinesReFetchFlag } = useContext(GroupsMachinesReFetchFlagContext);

  const navigate = useNavigate();

  useEffect(() => {
    setIsGroupDropdownLoading(true);
    if (Object.keys(groupDocument.groupDocument.groups).length <= 1) {
      setGroups(["None"]);
    } else {
      const temp = Object.keys(groupDocument.groupDocument.groups);
      // re-orders the names so "None" is always at the top, otherwise it is alphabetical
      for (let i = 0; i < temp.length; i++) {
        if (temp[i] == "None") {
          temp.splice(i, 1);
        }
      }
      temp.unshift("None");
      setGroups(temp);
    }
    setIsGroupDropdownLoading(false);
  }, [groupDocument]);

  const createGroupClicked = () => {
    accountType == "free" ? navigate("/dashboard/subscription") : setIsGroupModalVisible(true);
  };

  const onFinish = async (values: FormSubmit) => {
    setIsLoading(true);
    // console.log(`${JSON.stringify(values)}:    onFinish success`);
    const today = new Date();
    const dd = String(today.getDate()).padStart(2, "0");
    const mm = String(today.getMonth() + 1).padStart(2, "0"); //January is 0!
    const yyyy = today.getFullYear();
    const date = mm + "/" + dd + "/" + yyyy;
    // store and get first 3 values from B2C at later date
    const machineObject = {
      group: {
        group: values.group,
      },
      machine: {
        documentType: "machine",
        serialNumber: values.serial,
        machineName: values.displayName,
        creationDate: date,
      },
    };
    createMachineApi(machineObject)
      .then(function () {
        message.success("Your machine has been added successfully", 5);
        getGroupAndMachineDocuments(setGroupDocument, setMachineDocuments, setGroupsMachinesReFetchFlag);
        setIsLoading(false);
      })
      .catch((error: Error | AxiosError) => {
        if (axios.isAxiosError(error)) {
          console.log("AxiosError message: ", error);
          message.error("Failed to create machine: " + error.message, 5);
        } else {
          console.log("unexpected error: ", error);
          message.error("Failed to create machine: " + error, 5);
        }
        setIsLoading(false);
      });
  };

  const [form] = Form.useForm();
  useEffect(() => {
    form.setFieldsValue({
      group: selectedGroup,
    });
  }, [selectedGroup]);

  return (
    <div>
      {isLoading ? (
        <div style={{ textAlign: "center" }}>
          <Spin size="large" />
        </div>
      ) : (
        <div>
          <div className="connect-machine-title">Connect a new machine</div>
          <Form
            form={form}
            name="basic"
            layout="vertical"
            initialValues={{
              group: selectedGroup,
            }}
            onFinish={onFinish}
            autoComplete="off"
          >
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginBottom: 70,
              }}
            >
              <div className="new-machine-group-dropdown margin-right-60">
                <Form.Item name="group">
                  {/* OnChange and value are implicitly handled by the form */}
                  <Dropdown
                    title="Group"
                    isLoading={isGroupDropdownLoading}
                    isDisabled={accountType == "free" ? true : false}
                    onChange={(value) => setSelectedGroup(value as string)}
                    options={groups.map((e) => ({ label: e, value: e }))}
                    mainClassName="new-machine-group-dropdown"
                    value="this does nothing.Set by form initial values"
                  />
                </Form.Item>
                <span className="or-text">{accountType == "free" ? "To use groups," : "Or "}</span>
                <span className="link-style" onClick={createGroupClicked}>
                  {accountType == "free" ? "upgrade subscription" : "Create new group"}
                </span>
              </div>
              <div className="new-machine-group-dropdown margin-right-60">
                <Form.Item
                  label="Serial (Unique)"
                  name="serial"
                  rules={[
                    {
                      required: true,
                      message: "Please enter a serial #",
                    },
                  ]}
                >
                  <Input allowClear />
                </Form.Item>
              </div>
              <div className="new-machine-group-dropdown">
                <Form.Item
                  label="Display Name"
                  name="displayName"
                  rules={[
                    {
                      required: true,
                      message: "Please enter a display name",
                    },
                  ]}
                >
                  <Input allowClear />
                </Form.Item>
              </div>
            </div>
            <div className="btn-grp-wrapper">
              <Button
                className="primary-button"
                onClick={() =>
                  downloadFile({
                    data: JSON.stringify(sampleMessage, null, 4),
                    fileName: "sampleMessage.json",
                    fileType: "json",
                  })
                }
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-around",
                    columnGap: 10,
                  }}
                >
                  <Download />
                  Sample Message
                </div>
              </Button>
              <Form.Item>
                <Button className="secondary-button" htmlType="submit">
                  Connect Machine
                </Button>
              </Form.Item>
            </div>
          </Form>

          <CreateNewGroupPopup
            groups={groups}
            visible={isGroupModalVisible}
            closeModal={() => setIsGroupModalVisible(false)}
            setGroups={setGroups}
            setSelectedGroup={setSelectedGroup}
          />
        </div>
      )}
    </div>
  );
};

export default NewMachine;
